import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import { ColDef, GridApi, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { MenuModule } from "@ag-grid-enterprise/menu/dist/cjs/menuModule";
import { useHistory } from "react-router-dom";

import { EntityType } from "@mapmycustomers/shared/enum";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";

import { StandardFilterButton } from "@app/component/filtering";
import ActivityAnnotationLinkCellRenderer from "@app/component/grid/gridComponents/cellRenderers/ActivityAnnotationLinkCellRenderer";
import CountryCellRenderer from "@app/component/grid/gridComponents/cellRenderers/CountryCellRenderer";
import CrmLinkCellRenderer from "@app/component/grid/gridComponents/cellRenderers/CrmLinkCellRenderer";
import FrequencyCellRenderer from "@app/component/grid/gridComponents/cellRenderers/FrequencyCellRenderer";
import FrequencyIntervalCellRenderer from "@app/component/grid/gridComponents/cellRenderers/FrequencyIntervalCellRenderer";
import GroupsCellRenderer from "@app/component/grid/gridComponents/cellRenderers/GroupsCellRenderer";
import LocationCellRenderer from "@app/component/grid/gridComponents/cellRenderers/LocationCellRenderer";
import MultiOptionCustomFieldCellRenderer from "@app/component/grid/gridComponents/cellRenderers/MultiOptionCustomFieldCellRenderer";
import NameCellRenderer from "@app/component/grid/gridComponents/cellRenderers/NameCellRenderer";
import NotesCellRenderer from "@app/component/grid/gridComponents/cellRenderers/NotesCellRenderer";
import RottingCellRenderer from "@app/component/grid/gridComponents/cellRenderers/RottingCellRenderer";
import StageCellRenderer from "@app/component/grid/gridComponents/cellRenderers/StageCellRenderer";
import TeamsCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TeamsCellRenderer";
import TerritoriesCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TerritoriesCellRenderer";
import UserCellRenderer from "@app/component/grid/gridComponents/cellRenderers/UserCellRenderer";
import { DealGroupFilter } from "@app/component/grid/gridComponents/customFilter/GroupsFilter";
import OptionsFilter from "@app/component/grid/gridComponents/customFilter/OptionsFilter";
import { DealGroupsFilterPanel } from "@app/component/grid/gridComponents/toolPanels/GroupsFilterPanel";
import AgGridListView from "@app/component/view/ListView/AgGridListView";
import AgGridListViewServerSideGrid from "@app/component/view/ListView/AgGridListViewServerSideGrid";
import AgGridListViewPagination from "@app/component/view/ListView/components/Pagination/AgGridListViewPagination";
import SelectBar from "@app/component/view/SelectBar";
import Path from "@app/enum/Path";
import { isCurrentUserMember } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import dealFieldModel, { DealFieldName } from "@app/util/fieldModel/DealFieldModel";

import ViewMode from "../../enum/ViewMode";
import {
  getGridColumnDefs,
  getListViewState,
  getListViewTotalFilteredRecordsCount,
  getListViewTotalRecordsCount,
  getOfferedFilters,
  isListViewLoading,
} from "../../store";
import {
  fetchList,
  initializeListView,
  setOfferedFilters,
  subscribeOnViewStateUpdating,
  unsubscribeOnViewStateUpdating,
} from "../../store/actions";
import dealTableAnalytics, { dealListViewAnalytics } from "../../util/dealTableAnalytics";
import filterOperatorsForListAndForecast from "../../util/filterOperatorsForListAndForecast";
import Subheader from "../Subheader";
import Toolbar from "../Toolbar";
import QuickMultiFunnelFilter from "../Toolbar/QuickMultiFunnelFilter";
import QuickOwnerFilter from "../Toolbar/QuickOwnerFilter";
import QuickSearchFilter from "../Toolbar/QuickSearchFilter";

import DealListAggregateDataView from "./component/DealListAggregateDataView";
import DealListQuickFilters from "./component/DealListQuickFilters";
import styles from "./DealListPage.module.scss";

interface Props {
  columnDefs: ColDef[];
  initialize: typeof initializeListView.request;
  isMember?: boolean;
  listFetcher: typeof fetchList.request;
  listViewState: ViewState;
  loading?: boolean;
  offerFilters?: Partial<FilterModel>;
  setOfferedFilters: typeof setOfferedFilters;
  subscribe: typeof subscribe;
  subscribeOnViewStateUpdating: typeof subscribeOnViewStateUpdating;
  totalFilteredRecords: number;
  totalRecords: number;
  unsubscribe: typeof unsubscribe;
  unsubscribeOnViewStateUpdating: typeof unsubscribeOnViewStateUpdating;
}

const DealListPage: React.FC<Props> = ({
  columnDefs,
  initialize,
  isMember,
  listFetcher,
  listViewState,
  loading,
  offerFilters,
  setOfferedFilters,
  subscribe,
  subscribeOnViewStateUpdating,
  totalFilteredRecords,
  totalRecords,
  unsubscribe,
  unsubscribeOnViewStateUpdating,
}) => {
  const history = useHistory();

  useEffect(() => {
    initialize();
  }, [initialize]);

  const [selectedEntities, setSelectedEntities] = useState<Set<Deal>>(new Set());

  const handleRowSelectionChanged = useDebouncedCallback(
    [({ api }: RowSelectedEvent) => setSelectedEntities(new Set(api.getSelectedRows())), 250],
    [setSelectedEntities]
  );

  const gridApi = useRef<GridApi | undefined>(undefined);
  const handleGridReady = useCallback((event: GridReadyEvent) => (gridApi.current = event.api), []);

  useEffect(() => {
    // reset selected rows when filter changes
    gridApi.current?.deselectAll();
  }, [listViewState.filter]);

  const handleReloadData = useCallback(() => {
    gridApi.current?.deselectAll();
    const sortModel = gridApi.current?.getSortModel();
    if (
      sortModel &&
      sortModel.length &&
      sortModel[0].colId === "updatedAt" &&
      sortModel[0].sort === "desc"
    ) {
      gridApi.current?.onFilterChanged(); // force reload and go to the first page
    } else {
      gridApi.current?.onSortChanged(); // force reload
    }
  }, []);

  useEffect(() => {
    // TODO: use more sophisticated logic in a future, e.g. only reload if deleted deal exists in a current view
    const listener = {
      onAdd: () => handleReloadData(),
      onDelete: () => handleReloadData(),
      onUpdate: () => handleReloadData(),
    };
    subscribe({ entityTypes: [EntityType.DEAL], listener });
    return () => {
      unsubscribe({ entityTypes: [EntityType.DEAL], listener });
    };
  }, [handleReloadData, subscribe, unsubscribe]);

  useEffect(() => {
    const callback = (state: Partial<ViewState>) => {
      listFetcher({ request: state });
      handleReloadData();
    };
    subscribeOnViewStateUpdating({ callback });
    return () => {
      unsubscribeOnViewStateUpdating();
    };
  }, [listFetcher, handleReloadData, subscribeOnViewStateUpdating, unsubscribeOnViewStateUpdating]);

  const handleViewClick = useCallback(
    (deal: Deal) => {
      history.push(`${Path.DEAL}/${ViewMode.LIST}/${deal.id}`);
      dealTableAnalytics.clicked(["View"], { deal });
    },
    [history]
  );

  const hiddenFilterFields = useMemo(
    () =>
      dealFieldModel.fields
        .filter(
          (field) =>
            (field.hasFeature(FieldFeature.NON_LIST_VIEW) &&
              !field.hasAnyFeature(FieldFeature.VIEW_AS_FIELD, FieldFeature.TEAM_FIELD)) ||
            (isMember && field.hasFeature(FieldFeature.VIEW_AS_FIELD))
        )
        .filter(isDefined),
    [isMember]
  );

  const implicitFilterFields = useMemo(
    () => [dealFieldModel.getByName(DealFieldName.QUICK_FILTER)].filter(isDefined),
    []
  );

  const hiddenSavedFilterFields = useMemo(
    () =>
      [
        dealFieldModel.getByName(DealFieldName.QUICK_FILTER),
        dealFieldModel.getByName("funnel"),
      ].filter(isDefined),
    []
  );

  const handleClearOfferFilters = useCallback(() => {
    setOfferedFilters(undefined);
  }, [setOfferedFilters]);

  return (
    <AnalyticsContext.Provider value={dealListViewAnalytics}>
      <AgGridListView<Deal>
        columnDefs={columnDefs}
        entityType={EntityType.DEAL}
        fieldModel={dealFieldModel}
        listFetcher={listFetcher}
        listViewState={listViewState}
        totalRecords={totalRecords}
      >
        <Subheader totalFilteredRecords={totalFilteredRecords} totalRecords={totalRecords} />
        <section className={styles.listViewGrid}>
          {selectedEntities.size > 0 && (
            <SelectBar
              entityType={EntityType.DEAL}
              onReloadData={handleReloadData}
              selectedEntities={selectedEntities}
              visible
            />
          )}
          <Toolbar
            extra={[
              <QuickSearchFilter key="searchFilter" />,
              <QuickOwnerFilter key="ownerFilter" />,
              <QuickMultiFunnelFilter key="multiFunnelFilter" />,
            ]}
            filterButton={
              <StandardFilterButton
                context="dealPage"
                entityType={EntityType.DEAL}
                filterOperators={filterOperatorsForListAndForecast}
                hiddenFields={hiddenFilterFields}
                implicitFilterFields={implicitFilterFields}
                offerFilters={offerFilters}
                onHidePopover={handleClearOfferFilters}
              />
            }
            viewMode={ViewMode.LIST}
          />
          <DealListQuickFilters implicitFilterFields={hiddenSavedFilterFields} />
          <DealListAggregateDataView />
          <AgGridListViewServerSideGrid
            className={styles.tableContainer}
            context={{
              onView: handleViewClick,
            }}
            enableCellTextSelection // Note: When this is set to true, the clipboard service is disabled.
            frameworkComponents={{
              activityAnnotationLinkCellRenderer: ActivityAnnotationLinkCellRenderer,
              countryCellRenderer: CountryCellRenderer,
              crmLinkCellRenderer: CrmLinkCellRenderer,
              frequencyCellRenderer: FrequencyCellRenderer,
              frequencyIntervalCellRenderer: FrequencyIntervalCellRenderer,
              groupsCellRenderer: GroupsCellRenderer,
              groupsFilter: DealGroupFilter,
              groupsFilterPanel: DealGroupsFilterPanel,
              locationCellRenderer: LocationCellRenderer,
              multiOptionCustomFieldCellRenderer: MultiOptionCustomFieldCellRenderer,
              nameCellRenderer: NameCellRenderer,
              notesCellRenderer: NotesCellRenderer,
              optionsFilter: OptionsFilter,
              rottingCellRenderer: RottingCellRenderer,
              stageCellRenderer: StageCellRenderer,
              teamsCellRenderer: TeamsCellRenderer,
              territoriesCellRenderer: TerritoriesCellRenderer,
              userCellRenderer: UserCellRenderer,
            }}
            modules={[MenuModule]}
            onGridReady={handleGridReady}
            onSelectionChanged={handleRowSelectionChanged}
            paginationPageSize={listViewState.range.endRow - listViewState.range.startRow}
            showNoRows={!totalFilteredRecords && !loading}
            suppressPaginationPanel
          />
        </section>
        <AgGridListViewPagination showSizeChanger totalRecords={totalFilteredRecords} />
      </AgGridListView>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  columnDefs: getGridColumnDefs(state)(false),
  isMember: isCurrentUserMember(state),
  listViewState: getListViewState(state),
  loading: isListViewLoading(state),
  offerFilters: getOfferedFilters(state),
  totalFilteredRecords: getListViewTotalFilteredRecordsCount(state),
  totalRecords: getListViewTotalRecordsCount(state),
});

const mapDispatchToProps = {
  initialize: initializeListView.request,
  listFetcher: fetchList.request,
  setOfferedFilters,
  subscribe,
  subscribeOnViewStateUpdating,
  unsubscribe,
  unsubscribeOnViewStateUpdating,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealListPage);
